import { CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux'
import Workspace from 'src/entities/workspace';
import { fetchPricingPlans } from 'src/actions/pricing';
import { getPricingPlanWithFeature } from 'src/selectors/pricingPlans';
import { trackClickedPaywallChip } from 'src/services/tracker/trackers/paywall';
import { snakeCaseToTitleCase } from 'src/lib/stringUtils';

type UseFeaturePaywallParams = {
	feature: string;
	workspace: Workspace;
	origin?: string;
};

const useFeaturePaywall = ( {
	feature, workspace, origin
}: UseFeaturePaywallParams ) => {
	const dispatch = useDispatch();
	const [
		isShowingSubscriptionPlanSelectionModal, setIsShowingSubscriptionPlanSelectionModal
	] = useState( false );

	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-expect-error
	const planWithRequiredFeature = useSelector( state => getPricingPlanWithFeature( state, { feature } ) );

	const isFeatureAvailable = workspace?.supportsFeature( feature ) || false;
	const canUpgradeSubscriptionPlan = workspace?.currentUserRole?.subscriptionPermissions?.edit || false;
	const trackingName = snakeCaseToTitleCase( feature );

	const shouldDisableActions = !isFeatureAvailable && !canUpgradeSubscriptionPlan;
	const isOwner = workspace?.currentUserRole?.isOwner;

	const paywallMessage = useMemo( () => {
		const baseMessage = `This feature is only available with a ${planWithRequiredFeature?.name || 'Studio Plan'}.`;

		if ( isOwner ) {
			return `${baseMessage}\nClick to upgrade.`;
		}

		return `${baseMessage}\nRequest upgrade from owner.`;
	}, [ planWithRequiredFeature?.name, isOwner ] );

	const onRequiredPlanClick = useCallback( () => {
		if ( isFeatureAvailable ) return;

		trackClickedPaywallChip( {
			planName: planWithRequiredFeature?.name || 'Studio Plan',
			feature: trackingName || feature,
			origin
		} );

		setIsShowingSubscriptionPlanSelectionModal( true );
	}, [ planWithRequiredFeature?.name, isFeatureAvailable ] );

	const onSubscriptionPlanSelectionModalClose = useCallback( () => {
		setIsShowingSubscriptionPlanSelectionModal( false );
	}, [] );

	const planButtonStyles = useMemo( () => ( {
		display: 'flex',
		alignItems: 'center',
		padding: 0
	} as CSSProperties ), [] );

	useEffect( () => {
		if ( planWithRequiredFeature ) return;

		dispatch( fetchPricingPlans() );
	}, [ dispatch, planWithRequiredFeature ] );

	return {
		planButtonStyles,
		planWithRequiredFeature,
		shouldDisableActions,
		isShowingSubscriptionPlanSelectionModal,
		paywallMessage,
		onRequiredPlanClick,
		onSubscriptionPlanSelectionModalClose,
		isOwner,
		isFeatureAvailable
	};
};

export default useFeaturePaywall;
